import React, {MouseEvent, useEffect, useState} from "react";
import { MenuItem, Recommendation } from "../../../@types/stores/menu";
import {
    addToLog,
    getAllergenById,
    getCurrency, getCurrentCategory, getCurrentSubCategory,
    getHost,
    getPreferenceById,
    isCombinationOrderLine
} from "../../../helpers/ApiHelper";
import defaultImg from "../../../assets/images/default.webp";
import { useNavigate } from "react-router";
import { moveToTop } from "../../../helpers/DomHelper";
import { ArticleViewType } from "../../../@types/enums/ArticleViewType";
import { hideLoader, showLoader } from "../../../helpers/ComponentHelper";
import orderStore from "../../../stores/order";
import { v4 as guid } from 'uuid';
import menuStore from "../../../stores/menu";
import ArticleInfoBlock from "../../ArticleInfoBlock";
import { ReactComponent as IconInfo } from "../../../assets/icons/info.svg";
import {CombinationOrderLine} from "../../../@types/stores/order";
import lodash from "lodash";
import useLayoutStyles from "../../../hooks/styles";

export interface RecommendationGroupProps {
    group: Recommendation;
}

const RecommendationGroup = ({ group }: RecommendationGroupProps) => {
    const navigate = useNavigate();
    const styles = useLayoutStyles();

    const [actualGroup, setActualGroup] = useState<Recommendation>(group);

    useEffect(() => {
        modifyGroup();
    }, []);

    const modifyGroup = () => {
        const lastOrderLine = orderStore.order.lines.at(-1);
        if (!lastOrderLine || !isCombinationOrderLine(lastOrderLine)) {
            setActualGroup(group);
            return;
        }

        const orderCombination = lastOrderLine as CombinationOrderLine;
        if (orderCombination) {
            const _group = lodash.cloneDeep(group);
            orderCombination.steps.forEach(step => {
                step.articles.forEach(article => {
                    _group.articles = _group.articles.filter(x => x.id !== article.articleId);
                });
            });
            setActualGroup(_group);
        }
    }

    const goTo = (item: MenuItem, groupId: number) => {
        moveToTop();

        const category = getCurrentCategory();
        const subCategory = getCurrentSubCategory();
        addToLog(`Click on article #${item.id} / ${item.number} / ${item.name}, category #${category?.id || "<undefined>"} / ${category?.name || "<undefined>"}, subcategory #${subCategory?.id} / ${subCategory?.name || "<undefined>"}`);

        // ONE_PAGE
        if (!localStorage.getItem("articleViewType") || localStorage.getItem("articleViewType") === ArticleViewType.ONE_PAGE) {
            navigate(`/recommendations/${groupId}/${item.number}`);
        }

        // GROUP_PAGE
        else if (localStorage.getItem("articleViewType") === ArticleViewType.GROUP_PAGE) {
            if (item.modifierGroups?.length) {
                navigate(`/recommendations/${groupId}/${item.number}/modifiers`);
                return;
            }
            else if (item.addonGroups?.length) {
                navigate(`/recommendations/${groupId}/${item.number}/addons`);
                return;
            }
            else if (item.ingredientGroups?.length) {
                navigate(`/recommendations/${groupId}/${item.number}/ingredients`);
                return;
            }
            else {
                addToOrder(item, groupId);
                return;
            }
        }

        // INDIVIDUAL_PAGE
        else if (localStorage.getItem("articleViewType") === ArticleViewType.INDIVIDUAL_PAGE) {
            if (item.modifierGroups?.length) {
                navigate(`/recommendations/${groupId}/${item.number}/modifiers/${item.modifierGroups.at(0)?.id}`);
                return;
            }
            else if (item.addonGroups?.length) {
                navigate(`/recommendations/${groupId}/${item.number}/addons/${item.addonGroups.at(0)?.id}`);
                return;
            }
            else if (item.ingredientGroups?.length) {
                navigate(`/recommendations/${groupId}/${item.number}/ingredients`);
                return;
            }
            else {
                addToOrder(item, groupId);
                return;
            }
        }

        navigate(`/recommendations/${groupId}/${item.number}`);
    }

    const addToOrder = async (item: MenuItem, groupId: number) => {
        showLoader();
        const r = await orderStore.addToOrder({
            id: guid(),
            menuId: menuStore.navigation.category,
            subMenuId: menuStore.navigation.subCategory,
            recommendationGroupId: groupId,
            name: item.name,
            number: item.number,
            count: 1,
            price: item.price,
            total: item.price,
            img: item.previewList
        });
        if (r) {
            goNext(groupId);
        }
        hideLoader();
    }

    const goNext = (groupId: number) => {
        if (menuStore.navigation.item) {
            const item = menuStore.getItemById(menuStore.navigation.item);

            if (item?.recommendedGroups?.length) {
                const nextGroupIdIndex = item.recommendedGroups.findIndex(x => x === groupId) + 1;
                if (nextGroupIdIndex && nextGroupIdIndex < item.recommendedGroups.length) {
                    const nextGroupId = item.recommendedGroups.at(nextGroupIdIndex);
                    navigate(`/recommendations/${nextGroupId}`);
                    return;
                }
            }
        }

        navigate("/menu");
    }

    const openInfo = (e: MouseEvent<SVGSVGElement>, item: MenuItem, groupId: number) => {
        e.stopPropagation();
        menuStore.changeRecommendationGroup(groupId)
        menuStore.changeRecommendationItem(item.id);
    }

    return <>
        {localStorage.getItem("articleViewType") === ArticleViewType.GROUP_PAGE || localStorage.getItem("articleViewType") === ArticleViewType.INDIVIDUAL_PAGE
            ? <ArticleInfoBlock isRecommendationPage={true} />
            : null
        }
        <div key={`recommendationGroup_${actualGroup.id}`} className="group">
            <div className="groupTitle">
                {actualGroup.name}
            </div>
            <div className="items">
                {actualGroup.articles?.map(r => <div key={`recommendationGroup_${actualGroup.id}_item_${r.id}`} className="item" onClick={() => goTo(r, actualGroup.id)}>
                    <div className="tagsImages">
                        {r.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).map(y => getAllergenById(y)?.image ? <img key={`allergenTagImage_${y}`} src={`${getHost()}${getAllergenById(y)?.image || ""}`} alt={getAllergenById(y)?.name} /> : null)}
                        {r.preferences?.filter(y => menuStore.filter.preferences.indexOf(y) !== -1).map(y => getPreferenceById(y)?.image ? <img key={`preferenceTagImage_${y}`} src={`${getHost()}${getPreferenceById(y)?.image || ""}`} alt={getPreferenceById(y)?.name} /> : null)}
                    </div>
                    <div className={`img ${r.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).length ? "blur" : ""}`}>
                        <img alt={r.name} src={r.previewList ? `${getHost()}${r.previewList}` : (styles?.article.defaultListImage ? `${getHost()}${styles.article.defaultListImage}` : defaultImg)} />
                    </div>
                    <div className="text">
                        <div className="tagContainer">
                            <div className="tags">
                                {r.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).map(y => getAllergenById(y) ? <div key={`allergenTag_${y}`} className="tag allergen">{getAllergenById(y)?.name || ""}</div> : null)}
                                {r.preferences?.filter(y => menuStore.filter.preferences.indexOf(y) !== -1).map(y => getPreferenceById(y) ? <div key={`preferenceTag_${y}`} className="tag preference">{getPreferenceById(y)?.name || ""}</div> : null)}
                            </div>
                        </div>
                        <div className="title">
                            {r.name}
                        </div>
                        <div className="itemFooter">
                            {!localStorage.getItem("articleViewType") || localStorage.getItem("articleViewType") === ArticleViewType.ONE_PAGE
                                ? <div />
                                : <IconInfo onClick={(e) => openInfo(e, r, actualGroup.id)} />
                            }
                            <div className="price">
                                {r.price} {getCurrency()}
                            </div>
                        </div>
                    </div>
                </div>)}
            </div>
        </div>
    </>
}

export default RecommendationGroup;
